import {
  confirmDialog,
  CustomButton,
  CustomCollapseCard,
  CustomImage,
  CustomPagination,
  CustomTable,
  successDialog,
  Text,
  useText,
} from '@src/components';
import cardsRegistered from '@src/assets/icons/icon-cards-registered.svg';
import React, { useCallback, useEffect } from 'react';
import '@src/styles/draftOrder/draftList.scss';
import { useDispatch, useSelector } from 'react-redux';
import { cardOrdersUpdate, getDraftedCardOrders } from '@src/store/slice/cardOrderSlice';
import { moneyFormat } from '@src/utils/utils';
import { useHistory } from 'react-router-dom';
import dayjs from '@src/utils/day';
import { defaultDateFormat } from '@src/utils/keys';

const NewOrderDraftList = () => {
  const dispatch = useDispatch();
  const history = useHistory();

  const { draftedCardOrders, draftedTableProperty } = useSelector((state) => state?.cardOrder);

  const yourTransactionHasBeenSuccessfullyCompleted = useText('yourTransactionHasBeenSuccessfullyCompleted');
  const newOrderDeleteDraftConfirmation = useText('newOrder.deleteDraftConfirmation');
  const loadNewCardOrders = useCallback(
    async (pageNumber, pageSize) => {
      dispatch(getDraftedCardOrders({ pageNumber, pageSize }));
    },
    [dispatch],
  );

  const handleTableChange = useCallback(
    async (pageNumber, pageSize) => {
      await loadNewCardOrders(pageNumber, pageSize);
    },
    [loadNewCardOrders],
  );

  useEffect(() => {
    (async () => {
      await loadNewCardOrders(1, 10);
    })();
  }, [loadNewCardOrders]);

  const acceptDraft = async (record) => {
    history.push(`/ticket-restaurant/draft-order/card-orders/${record?.id}`);
  };

  const deleteDraft = async (record) => {
    confirmDialog({
      title: <Text t="attention" />,
      message: newOrderDeleteDraftConfirmation,
      okText: <Text t="delete" />,
      cancelText: <Text t="giveUp" />,
      onOk: async () => {
        let data = {
          id: record.id,
          draft: false,
        };

        const action = await dispatch(cardOrdersUpdate(data));
        if (cardOrdersUpdate.fulfilled.match(action)) {
          successDialog({
            title: <Text t="successfullySent" />,
            message: action?.payload?.message || yourTransactionHasBeenSuccessfullyCompleted,
            onOk: () => {
              const { currentPage, pageSize } = draftedTableProperty;

              if (currentPage > 1 && draftedCardOrders.length <= 1) {
                loadNewCardOrders(currentPage - 1, pageSize);
              } else {
                loadNewCardOrders(currentPage, pageSize);
              }
            },
          });
        }
      },
    });
  };

  const columns = [
    {
      title: <Text t="orderNoUpperCase" />,
      dataIndex: 'id',
      key: 'id',
      fixed: 'left',
      align: 'center',
      width: 100,
    },
    {
      title: <Text t="orderDate" />,
      dataIndex: 'insertTime',
      key: 'insertTime',
      width: 162.7,
      render: (text) => {
        return text ? dayjs(text).format(defaultDateFormat) : '-';
      },
    },
    {
      title: <Text t="totalCardsUpperCase" />,
      dataIndex: 'totalCount',
      key: 'totalCount',
      align: 'center',
      width: 162.7,
    },
    {
      title: <Text t="cardType" />,
      dataIndex: 'cardType',
      key: 'cardType',
      width: 150,
      align: 'center',
      hidden: true,
      render: (_, record) => {
        return <div>{record.cardType === 0 ? <Text t="classicCard" /> : <Text t="digitalCard" />}</div>;
      },
    },
    {
      title: <Text t="orderPrice" />,
      dataIndex: 'totalPaymentPrice',
      key: 'totalPaymentPrice',
      width: 150,
      align: 'center',
      render: (text) => <span>{text ? `${moneyFormat(text, false)}₺` : '0'}</span>,
    },
    {
      title: <Text t="viewOrder" />,
      width: 162.7,
      dataIndex: 'orderViewAction',
      key: 'orderViewAction',
      align: 'center',
      render: (_, record) => {
        return (
          <div className="action-btns">
            <CustomButton
              id="ticketRestaurant-draftOrders-newOrderDraftList-detail-btn"
              className="detail-btn"
              onClick={() => acceptDraft(record)}
            >
              <Text t="viewOrder" />
            </CustomButton>
          </div>
        );
      },
    },
    {
      title: <Text t="delete" />,
      dataIndex: 'draftDeleteAction',
      key: 'draftDeleteAction',
      width: 100,
      align: 'center',
      render: (_, record) => {
        return (
          <div className="action-btns">
            <CustomButton
              id="ticketRestaurant-draftOrders-newOrderDraftList-delete-btn"
              className="delete-btn"
              onClick={() => deleteDraft(record)}
            >
              <Text t="delete" />
            </CustomButton>
          </div>
        );
      },
    },
  ];

  return (
    <CustomCollapseCard className="draft-list-card" cardTitle={<Text t="newOrderDrafts" />}>
      <div className="number-registered-drafts">
        <CustomImage src={cardsRegistered} />
        <div className="drafts-count-title">
          <Text t="registeredOrderDraft" /> <span>{draftedTableProperty?.totalCount}</span>
        </div>
      </div>

      <CustomTable
        pagination={false}
        dataSource={draftedCardOrders}
        columns={columns}
        rowKey={(record) => `draft-list-new-order-${record?.id || record?.name}`}
      />

      <div className="draft-list-footer">
        <CustomPagination
          current={draftedTableProperty?.currentPage}
          defaultCurrent={1}
          pageSize={draftedTableProperty?.pageSize}
          total={draftedTableProperty?.totalCount}
          onChange={async (pageNumber, pageSize) => {
            await handleTableChange(pageNumber, pageSize);
          }}
        />
      </div>
    </CustomCollapseCard>
  );
};

export default NewOrderDraftList;
